import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Music, 
  Video, 
  Image, 
  Link as LinkIcon,
  Download, 
  Eye, 
  Search,
  Filter,
  Calendar,
  User,
  Tag,
  BookOpen,
  Heart,
  MessageCircle,
  Church,
  Star,
  ExternalLink,
  Play,
  Pause,
  Volume2,
  SkipBack,
  SkipForward,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Bookmark,
  Clock,
  Grid3X3,
  List,
  SortAsc,
  SortDesc,
  HeadphonesIcon,
  PlayCircle,
  FileType,
  Library,
  Sparkles,
  X
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';

// Fonction utilitaire pour construire correctement les URLs de fichiers
const getFileUrl = (resource) => {
  if (resource.file_path) {
    return `http://localhost:8000/storage/${resource.file_path}`;
  } else if (resource.file_url) {
    // Si l'URL commence par http, l'utiliser telle quelle
    if (resource.file_url.startsWith('http')) {
      return resource.file_url;
    }
    // Si l'URL est relative (commence par /), ajouter le domaine
    return `http://localhost:8000${resource.file_url}`;
  }
  return null;
};

// Fonction utilitaire pour gérer les tags (peut être une chaîne ou un tableau)
const getTags = (resource) => {
  if (!resource.tags) return [];
  if (typeof resource.tags === 'string') {
    // Si c'est une chaîne, essayer de la parser comme JSON ou la diviser par des virgules
    try {
      const parsed = JSON.parse(resource.tags);
      return Array.isArray(parsed) ? parsed : [resource.tags];
    } catch (e) {
      // Si ce n'est pas du JSON valide, diviser par des virgules
      return resource.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    }
  }
  return Array.isArray(resource.tags) ? resource.tags : [];
};

const ResourcesPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid, list, library
  const [sortBy, setSortBy] = useState('recent'); // recent, title, author, popular
  const [favorites, setFavorites] = useState(JSON.parse(localStorage.getItem('fsf_favorites') || '[]'));
  const [currentMedia, setCurrentMedia] = useState(null); // Pour le lecteur audio/vidéo
  const [isPlaying, setIsPlaying] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [localStats, setLocalStats] = useState({}); // Compteurs locaux en temps réel

  const openContactModal = () => setIsContactModalOpen(true);
  const closeContactModal = () => setIsContactModalOpen(false);

  // Configuration des types de ressources avec design amélioré
  const typeOptions = {
    document: { 
      label: 'Documents', 
      icon: FileText, 
      color: 'text-blue-600',
      bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
      borderColor: 'border-blue-200',
      gradient: 'from-blue-500 to-blue-600'
    },
    audio: { 
      label: 'Audio', 
      icon: HeadphonesIcon, 
      color: 'text-green-600',
      bgColor: 'bg-gradient-to-br from-green-50 to-emerald-100',
      borderColor: 'border-green-200',
      gradient: 'from-green-500 to-emerald-600'
    },
    video: { 
      label: 'Vidéos', 
      icon: PlayCircle, 
      color: 'text-red-600',
      bgColor: 'bg-gradient-to-br from-red-50 to-rose-100',
      borderColor: 'border-red-200',
      gradient: 'from-red-500 to-rose-600'
    },
    image: { 
      label: 'Images', 
      icon: Image, 
      color: 'text-amber-500',
      bgColor: 'bg-gradient-to-br from-amber-50 to-amber-100',
      borderColor: 'border-amber-200',
      gradient: 'from-amber-500 to-amber-600'
    },
    link: { 
      label: 'Liens', 
      icon: ExternalLink, 
      color: 'text-orange-600',
      bgColor: 'bg-gradient-to-br from-orange-50 to-amber-100',
      borderColor: 'border-orange-200',
      gradient: 'from-orange-500 to-amber-600'
    }
  };

  const categoryOptions = {
    formation: { label: 'Formation', icon: BookOpen, color: 'bg-blue-100 text-blue-800' },
    priere: { label: 'Prière', icon: Heart, color: 'bg-pink-100 text-pink-800' },
    temoignage: { label: 'Témoignage', icon: MessageCircle, color: 'bg-green-100 text-green-800' },
    liturgie: { label: 'Liturgie', icon: Church, color: 'bg-amber-100 text-amber-700' },
    catechese: { label: 'Catéchèse', icon: Star, color: 'bg-yellow-100 text-yellow-800' },
    spiritualite: { label: 'Spiritualité', icon: Heart, color: 'bg-indigo-100 text-indigo-800' },
    autre: { label: 'Autre', icon: FileText, color: 'bg-gray-100 text-gray-800' }
  };

  useEffect(() => {
    fetchResources();
  }, []);

  useEffect(() => {
    filterResources();
  }, [resources, searchTerm, selectedType, selectedCategory, sortBy]);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await fetch('http://localhost:8000/api/public/resources', {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setResources(data.data || []);
      } else {
        console.error('Erreur lors du chargement des ressources');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterResources = () => {
    let filtered = resources; // Pas besoin de filtrer par status car l'API le fait déjà

    // Filtre par recherche
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(resource =>
        resource.title.toLowerCase().includes(search) ||
        resource.description?.toLowerCase().includes(search) ||
        resource.author?.toLowerCase().includes(search) ||
        resource.tags?.some(tag => tag.toLowerCase().includes(search))
      );
    }

    // Filtre par type
    if (selectedType !== 'all') {
      filtered = filtered.filter(resource => resource.type === selectedType);
    }

    // Filtre par catégorie
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(resource => resource.category === selectedCategory);
    }

    // Tri
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'author':
          return (a.author || '').localeCompare(b.author || '');
        case 'popular':
          return (b.view_count + b.download_count) - (a.view_count + a.download_count);
        case 'recent':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    setFilteredResources(filtered);
  };

  // Gestion des favoris
  const toggleFavorite = (resourceId) => {
    const newFavorites = favorites.includes(resourceId) 
      ? favorites.filter(id => id !== resourceId)
      : [...favorites, resourceId];
    
    setFavorites(newFavorites);
    localStorage.setItem('fsf_favorites', JSON.stringify(newFavorites));
  };

  // Gestion du lecteur multimédia
  const openMediaPlayer = (resource) => {
    setCurrentMedia(resource);
    if (resource.type === 'video' || resource.type === 'audio') {
      setModalContent({
        type: 'media',
        resource
      });
      setShowModal(true);
    }
  };

  // Gestion de la visionneuse PDF
  const openPdfViewer = (resource) => {
    setModalContent({
      type: 'pdf',
      resource
    });
    setShowModal(true);
  };

  // Système de compteurs animés
  const updateLocalStats = (resourceId, type) => {
    setLocalStats(prev => ({
      ...prev,
      [resourceId]: {
        ...prev[resourceId],
        [type]: (prev[resourceId]?.[type] || 0) + 1
      }
    }));
  };

  const getResourceStats = (resource) => {
    const local = localStats[resource.id] || {};
    return {
      view_count: (resource.view_count || 0) + (local.view_count || 0),
      download_count: (resource.download_count || 0) + (local.download_count || 0)
    };
  };

  // Animation du compteur
  const animateCounter = (element, start, end, duration = 1000) => {
    if (!element) return;
    
    const range = end - start;
    const startTime = performance.now();
    
    // Effet visuel pendant l'animation
    const parentElement = element.parentElement;
    parentElement.classList.add('scale-110', 'bg-blue-100', 'text-blue-700', 'shadow-lg');
    
    const updateCounter = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Fonction d'easing pour une animation plus fluide
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(start + (range * easeOutQuart));
      
      element.textContent = current;
      
      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      } else {
        // Retour à l'état normal après l'animation
        setTimeout(() => {
          parentElement.classList.remove('scale-110', 'bg-blue-100', 'text-blue-700', 'shadow-lg');
        }, 300);
      }
    };
    
    requestAnimationFrame(updateCounter);
  };

  const handleResourceClick = async (resource) => {
    // Mise à jour immédiate du compteur local pour feedback instantané
    updateLocalStats(resource.id, 'view_count');
    
    // Animation du compteur
    const viewElement = document.querySelector(`[data-view="${resource.id}"]`);
    if (viewElement) {
      const stats = getResourceStats(resource);
      animateCounter(viewElement, stats.view_count - 1, stats.view_count);
    }

    // Incrémenter le compteur sur le serveur
    try {
      await fetch(`http://localhost:8000/api/public/resources/${resource.id}/view`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        }
      });
    } catch (error) {
      console.error('Erreur lors de l\'incrémentation des vues:', error);
    }

    // Gestion selon le type de ressource
    if (resource.type === 'audio' || resource.type === 'video') {
      openMediaPlayer(resource);
    } else if (resource.type === 'document' && resource.file_path?.endsWith('.pdf')) {
      openPdfViewer(resource);
    } else {
      const fileUrl = getFileUrl(resource);
      if (fileUrl) {
        window.open(fileUrl, '_blank');
      }
    }
  };

  const handleDownload = async (resource, e) => {
    e.stopPropagation();
    
    // Mise à jour immédiate du compteur local pour feedback instantané
    updateLocalStats(resource.id, 'download_count');
    
    // Animation du compteur
    const downloadElement = document.querySelector(`[data-download="${resource.id}"]`);
    if (downloadElement) {
      const stats = getResourceStats(resource);
      animateCounter(downloadElement, stats.download_count - 1, stats.download_count);
    }

    // Incrémenter le compteur sur le serveur
    try {
      await fetch(`http://localhost:8000/api/public/resources/${resource.id}/download-count`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        }
      });
    } catch (error) {
      console.error('Erreur lors de l\'incrémentation des téléchargements:', error);
    }

    // Télécharger le fichier
    const fileUrl = getFileUrl(resource);
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = resource.title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getTypeIcon = (type, className = `w-6 h-6 ${typeOptions[type]?.color || 'text-gray-600'}`) => {
    const TypeIcon = typeOptions[type]?.icon || FileText;
    return <TypeIcon className={className} />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatFileSize = (sizeInBytes) => {
    if (!sizeInBytes) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = sizeInBytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const ResourceCard = ({ resource }) => {
    const isFavorite = favorites.includes(resource.id);
    
    return (
      <div className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 ${typeOptions[resource.type]?.borderColor || 'border-gray-200'} hover:scale-105 overflow-hidden`}>
        {/* En-tête avec gradient */}
        <div className={`relative p-6 ${typeOptions[resource.type]?.bgColor || 'bg-gray-50'} border-b-2 border-white/20`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-full bg-gradient-to-br ${typeOptions[resource.type]?.gradient || 'from-gray-500 to-gray-600'} shadow-lg`}>
                {getTypeIcon(resource.type, 'w-6 h-6 text-white')}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-1 line-clamp-2">
                  {resource.title}
                </h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${categoryOptions[resource.category]?.color || 'bg-gray-100 text-gray-800'}`}>
                  {React.createElement(categoryOptions[resource.category]?.icon || FileText, { className: "w-3 h-3 mr-1" })}
                  {categoryOptions[resource.category]?.label || resource.category}
                </span>
              </div>
            </div>
            
            {/* Actions rapides */}
            <div className="flex flex-col space-y-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(resource.id);
                }}
                className={`p-2 rounded-full transition-colors ${isFavorite ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400 hover:text-red-600'}`}
              >
                <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
              {resource.file_url && (
                <ExternalLink className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </div>
        </div>

        {/* Corps de la carte */}
        <div className="p-6">
          {resource.description && (
            <p className="text-gray-600 text-sm mb-4 text-justify line-clamp-3 leading-relaxed">
              {resource.description}
            </p>
          )}

          {/* Métadonnées */}
          <div className="space-y-3 mb-4">
            {resource.author && (
              <div className="flex items-center text-sm text-gray-500">
                <div className="flex items-center bg-gray-50 px-3 py-1 rounded-full">
                  <User className="w-4 h-4 mr-2" />
                  <span className="font-medium">{resource.author}</span>
                </div>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              {resource.published_date && (
                <div className="flex items-center bg-blue-50 px-3 py-1 rounded-full text-sm text-blue-700">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>{formatDate(resource.published_date)}</span>
                </div>
              )}

              {resource.file_size && (
                <div className="flex items-center bg-amber-50 px-3 py-1 rounded-full text-sm text-amber-600">
                  <FileText className="w-4 h-4 mr-2" />
                  <span>{formatFileSize(resource.file_size)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          {(() => {
            const tags = getTags(resource);
            return tags.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-1">
                  {tags.slice(0, 3).map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 font-medium"
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </span>
                ))}
                {tags.length > 3 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 font-medium">
                    +{tags.length - 3} autres
                  </span>
                )}
              </div>
            </div>
            );
          })()}

          {/* Actions et statistiques */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center space-x-4 text-xs text-gray-400">
              <div className="flex items-center bg-gray-50 px-2 py-1 rounded-full hover:bg-blue-50 transition-colors">
                <Eye className="w-3 h-3 mr-1" />
                <span 
                  data-view={resource.id}
                  className="font-medium transition-all duration-300"
                >
                  {getResourceStats(resource).view_count}
                </span>
              </div>
              <div className="flex items-center bg-gray-50 px-2 py-1 rounded-full hover:bg-green-50 transition-colors">
                <Download className="w-3 h-3 mr-1" />
                <span 
                  data-download={resource.id}
                  className="font-medium transition-all duration-300"
                >
                  {getResourceStats(resource).download_count}
                </span>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex space-x-2">
              {(resource.type === 'audio' || resource.type === 'video') && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResourceClick(resource);
                  }}
                  className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r ${typeOptions[resource.type]?.gradient} hover:shadow-lg transition-all`}
                >
                  <Play className="w-4 h-4 mr-1" />
                  {resource.type === 'audio' ? 'Écouter' : 'Regarder'}
                </button>
              )}

              {resource.type === 'document' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResourceClick(resource);
                  }}
                  className="flex items-center px-3 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-lg transition-all"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Lire
                </button>
              )}

              {resource.file_path && (
                <button
                  onClick={(e) => handleDownload(resource, e)}
                  className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 hover:shadow-md transition-all"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Télécharger
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Badge du type de fichier */}
        <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${typeOptions[resource.type]?.gradient || 'from-gray-500 to-gray-600'} text-white shadow-lg`}>
          {typeOptions[resource.type]?.label || resource.type}
        </div>

        {/* Effet hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </div>
    );
  };

  const ResourceListItem = ({ resource }) => (
    <div 
      className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300 cursor-pointer group"
      onClick={() => handleResourceClick(resource)}
    >
      <div className="p-4">
        <div className="flex items-start space-x-4">
          <div className={`p-3 rounded-lg ${typeOptions[resource.type]?.bgColor || 'bg-gray-50'}`}>
            {getTypeIcon(resource.type)}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                  {resource.title}
                </h3>
                <div className="mt-1 flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryOptions[resource.category]?.color || 'bg-gray-100 text-gray-800'}`}>
                    {categoryOptions[resource.category]?.label || resource.category}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeOptions[resource.type]?.color || 'text-gray-600'} bg-gray-100`}>
                    {typeOptions[resource.type]?.label || resource.type}
                  </span>
                </div>
              </div>
              
              {resource.file_path && (
                <button
                  onClick={(e) => handleDownload(resource, e)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Télécharger
                </button>
              )}
            </div>
            
            {resource.description && (
              <p className="mt-2 text-gray-600 text-sm text-justify line-clamp-2">
                {resource.description}
              </p>
            )}
            
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                {resource.author && (
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    <span>{resource.author}</span>
                  </div>
                )}
                {resource.published_date && (
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    <span>{formatDate(resource.published_date)}</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-4 text-xs text-gray-400">
                <div className="flex items-center">
                  <Eye className="w-4 h-4 mr-1" />
                  <span>{resource.view_count}</span>
                </div>
                <div className="flex items-center">
                  <Download className="w-4 h-4 mr-1" />
                  <span>{resource.download_count}</span>
                </div>
              </div>
            </div>
            
            {(() => {
              const tags = getTags(resource);
              return tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {tags.slice(0, 5).map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600"
                  >
                    {tag}
                  </span>
                ))}
                {tags.length > 5 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                    +{tags.length - 5} autres
                  </span>
                )}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="resources-page min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header openContactModal={openContactModal} />
      
      {/* Modal de contact */}
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={closeContactModal} 
      />
      
      {/* En-tête de la page */}
      <div className="bg-gradient-to-br from-indigo-900 via-amber-800 to-pink-900 py-20 relative overflow-hidden">
        {/* Éléments décoratifs */}
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-blue-400/20 rounded-full blur-2xl"></div>
        
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-white/10 backdrop-blur-lg p-4 rounded-2xl">
              <Library className="w-12 h-12 text-white" />
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
            Bibliothèque Numérique FSF
          </h1>
          <p className="text-xl text-white/90 max-w-4xl mx-auto text-center leading-relaxed mb-8">
            Explorez notre collection spirituelle enrichissante : documents de formation, 
            méditations audio, vidéos inspirantes et ressources liturgiques pour nourrir votre foi.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center bg-white/10 backdrop-blur-lg px-4 py-2 rounded-full">
              <Sparkles className="w-5 h-5 text-yellow-300 mr-2" />
              <span className="text-white font-medium">Collection Spirituelle</span>
            </div>
            <div className="flex items-center bg-white/10 backdrop-blur-lg px-4 py-2 rounded-full">
              <Clock className="w-5 h-5 text-green-300 mr-2" />
              <span className="text-white font-medium">Accès 24/7</span>
            </div>
            <div className="flex items-center bg-white/10 backdrop-blur-lg px-4 py-2 rounded-full">
              <BookOpen className="w-5 h-5 text-blue-300 mr-2" />
              <span className="text-white font-medium">Formation Continue</span>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistiques rapides */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-12">
          {Object.entries(typeOptions).map(([type, config]) => {
            const count = resources.filter(r => r.type === type).length;
            if (count === 0) return null;
            return (
              <div 
                key={type} 
                className={`group ${config.bgColor} p-6 rounded-2xl border-2 ${config.borderColor} hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105`}
                onClick={() => setSelectedType(selectedType === type ? 'all' : type)}
              >
                <div className="text-center">
                  <div className={`inline-flex p-4 rounded-full bg-gradient-to-br ${config.gradient} shadow-lg mb-4 group-hover:scale-110 transition-transform`}>
                    <config.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{count}</div>
                  <div className="text-sm font-medium text-gray-600">{config.label}</div>
                  {selectedType === type && (
                    <div className="mt-2 text-xs text-blue-600 font-semibold">
                      ✓ Sélectionné
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Barre de contrôles avancée */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <Filter className="w-5 h-5 mr-2 text-blue-600" />
              Filtres et Recherche
            </h2>
            <div className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
              {filteredResources.length} ressource{filteredResources.length > 1 ? 's' : ''} trouvée{filteredResources.length > 1 ? 's' : ''}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            {/* Recherche */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="w-4 h-4 inline mr-1" />
                Recherche globale
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Titre, description, auteur, tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
                <Search className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Filtre par type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de contenu
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="all">🔍 Tous les types</option>
                {Object.entries(typeOptions).map(([value, config]) => (
                  <option key={value} value={value}>
                    {value === 'document' ? '📄' : value === 'audio' ? '🎵' : value === 'video' ? '🎥' : value === 'image' ? '🖼️' : '🔗'} {config.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre par catégorie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Catégorie spirituelle
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="all">🙏 Toutes les catégories</option>
                {Object.entries(categoryOptions).map(([value, config]) => (
                  <option key={value} value={value}>
                    {value === 'formation' ? '📚' : value === 'priere' ? '🙏' : value === 'temoignage' ? '💬' : value === 'liturgie' ? '⛪' : value === 'catechese' ? '⭐' : value === 'spiritualite' ? '💖' : '📝'} {config.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Tri */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trier par
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="recent">🕐 Plus récent</option>
                <option value="title">🔤 Titre A-Z</option>
                <option value="author">👤 Auteur A-Z</option>
                <option value="popular">🔥 Plus populaire</option>
              </select>
            </div>
          </div>

          {/* Sélecteur de vue et favoris */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Vue :</span>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all ${
                      viewMode === 'grid' 
                        ? 'bg-white text-blue-600 shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Grid3X3 className="w-4 h-4 mr-1" />
                    Grille
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all ${
                      viewMode === 'list' 
                        ? 'bg-white text-blue-600 shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <List className="w-4 h-4 mr-1" />
                    Liste
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Heart className="w-4 h-4 text-red-500" />
              <span>{favorites.length} favoris</span>
            </div>
          </div>
        </div>

        {/* Liste des ressources */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Chargement des ressources...</p>
          </div>
        ) : filteredResources.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune ressource trouvée</h3>
            <p className="text-gray-500">
              {searchTerm || selectedType !== 'all' || selectedCategory !== 'all'
                ? "Essayez de modifier vos critères de recherche."
                : "Les ressources seront bientôt disponibles."}
            </p>
          </div>
        ) : (
          <div className={
            viewMode === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              : "space-y-4"
          }>
            {filteredResources.map((resource) => (
              viewMode === 'grid' ? (
                <ResourceCard key={resource.id} resource={resource} />
              ) : (
                <ResourceListItem key={resource.id} resource={resource} />
              )
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showModal && modalContent && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden relative">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900">
                {modalContent.resource.title}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <div className="p-6">
              {modalContent.type === 'media' && (
                <div className="space-y-4">
                  {modalContent.resource.type === 'video' ? (
                    <video 
                      controls 
                      className="w-full max-h-96 rounded-lg"
                      src={getFileUrl(modalContent.resource)}
                    >
                      Votre navigateur ne supporte pas la lecture vidéo.
                    </video>
                  ) : (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-8 rounded-2xl">
                      <div className="flex items-center justify-center mb-6">
                        <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-4 rounded-full">
                          <HeadphonesIcon className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <audio 
                        controls 
                        className="w-full"
                        src={getFileUrl(modalContent.resource)}
                      >
                        Votre navigateur ne supporte pas la lecture audio.
                      </audio>
                    </div>
                  )}
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                    <p className="text-gray-600 text-justify">{modalContent.resource.description}</p>
                    {modalContent.resource.author && (
                      <p className="text-sm text-gray-500 mt-2">
                        <User className="w-4 h-4 inline mr-1" />
                        Par {modalContent.resource.author}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {modalContent.type === 'pdf' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-blue-700 mb-4">
                      <FileText className="w-5 h-5 inline mr-2" />
                      Document PDF - Cliquez pour ouvrir dans un nouvel onglet
                    </p>
                    <button
                      onClick={() => {
                        const fileUrl = getFileUrl(modalContent.resource);
                        if (fileUrl) window.open(fileUrl, '_blank');
                      }}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Ouvrir le PDF
                    </button>
                  </div>
                  
                  {/* Aperçu iframe pour les PDFs */}
                  <iframe
                    src={getFileUrl(modalContent.resource)}
                    className="w-full h-96 rounded-lg border"
                    title={modalContent.resource.title}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ResourcesPage;